import 'highlight.js/styles/github-dark.css';
import hljs from 'highlight.js/lib/core';
import c from 'highlight.js/lib/languages/c';
import json from 'highlight.js/lib/languages/json';
import plaintext from 'highlight.js/lib/languages/plaintext';
import { extractAllValidJson } from '@/helpers/codeDisplay';

export type CodeHighlightLanguage = 'plaintext' | 'c' | 'json';

let initialized = false;

function ensureInitialized(): void {
    if (initialized) return;
    hljs.registerLanguage('plaintext', plaintext);
    hljs.registerLanguage('c', c);
    hljs.registerLanguage('json', json);
    initialized = true;
}

/**
 * Highlight code with the specified language.
 * Returns HTML string with syntax highlighting.
 * May throw if content causes parsing issues.
 */
export function highlightCode(content: string, language: CodeHighlightLanguage): string {
    ensureInitialized();
    return hljs.highlight(content, { language }).value;
}

/**
 * Highlight code, returning fallback (or content) on error.
 */
export function highlightCodeSafe(content: string, language: CodeHighlightLanguage, fallback?: string): string {
    try {
        return highlightCode(content, language);
    } catch {
        return fallback ?? content;
    }
}

/**
 * Highlight log-like content (C-style syntax as primary, plaintext as fallback).
 * Never throws - returns raw content if both highlight attempts fail.
 */
export function highlightLogContent(content: string): string {
    if (!content) return '';
    ensureInitialized();
    try {
        return hljs.highlight(content, { language: 'c' }).value;
    } catch {
        try {
            return hljs.highlight(content, { language: 'plaintext' }).value;
        } catch {
            return content;
        }
    }
}

/**
 * Highlight content with embedded JSON (query strings, POST bodies, etc.).
 * Original text is preserved — no pretty-printing or reformatting.
 */
export function highlightMixedContent(content: string): string {
    if (!content) return '';

    try {
        JSON.parse(content);
        return highlightCodeSafe(content, 'json', content);
    } catch {
        // not a single JSON value — check for embedded fragments
    }

    const jsonObjects = extractAllValidJson(content);

    if (jsonObjects.length === 0) {
        return highlightCodeSafe(content, 'plaintext', content);
    }

    let result = '';
    let lastIndex = 0;

    for (const jsonObj of jsonObjects) {
        if (jsonObj.start > lastIndex) {
            const textBefore = content.substring(lastIndex, jsonObj.start);
            result += highlightCodeSafe(textBefore, 'plaintext', textBefore);
        }

        result += highlightCodeSafe(jsonObj.json, 'json', jsonObj.json);
        lastIndex = jsonObj.end + 1;
    }

    if (lastIndex < content.length) {
        const textAfter = content.substring(lastIndex);
        result += highlightCodeSafe(textAfter, 'plaintext', textAfter);
    }

    return result;
}
